import { View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { addDays, colors, fonts, WORKER_LANGS, type WorkerLang, formatLongDate, groupByRoom, isoOf, pick, tasksInPlan, ts, type Weekday } from '@hogar/shared';
import { Txt } from '../components/ui';
import { Logo } from '../components/Logo';
import { Box, Paper } from '../components/Paper';
import { useHouse } from '../lib/house';
import { crewSlice } from '../lib/crew';

/** Printable checklist for staff: always in Spanish, every task under its room (decision 17). */
export default function Checklist() {
  const { day, crew: crewParam, lang: langParam } = useLocalSearchParams<{ day?: string; crew?: string; lang?: string }>();
  // Printed in the worker's language (Spanish unless the crew's worker reads another).
  const wl: WorkerLang = (WORKER_LANGS as readonly string[]).includes(langParam ?? '') ? (langParam as WorkerLang) : 'es';
  const snap = useHouse();
  // The gardener's list has only the garden's areas and supplies; the house's only the house's.
  const crew = crewParam === 'garden' ? 'garden' : 'house';
  const { tasks, rooms, products } = crewSlice(snap, crew);
  const { household, today, todayDate } = snap;
  const d = (day !== undefined ? Number(day) : today.weekday) as Weekday;
  // The list is for the next date on that weekday, with any day move applied.
  const date = addDays(todayDate, (d - today.weekday + 7) % 7);
  const dayTasks = tasksInPlan(snap.plans[isoOf(date)]?.[crew], d, tasks);
  const groups = groupByRoom(dayTasks, rooms);

  return (
    <Paper>
      <Logo size={0.8} tagline={false} />
      <Txt weight="semibold" size={14} style={{ textAlign: 'center', marginTop: 10 }}>
        {formatLongDate(date, wl)}
      </Txt>
      <Txt size={12} color={colors.textMuted} style={{ textAlign: 'center', marginBottom: 14 }}>
        {dayTasks.length} {ts(wl, dayTasks.length === 1 ? 'taskOne' : 'tasksShort')}
      </Txt>

      <Heading>{ts(wl, 'checklistTasks')}</Heading>
      <View nativeID="print-columns">
      {groups.map((g) => (
        <View key={g.room.id} style={{ marginBottom: 8 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
            <Txt weight="semibold" size={13}>
              {pick(g.room.name, wl, 'usted')}
            </Txt>
          </View>
          {g.tasks.map((tk) => (
            <View key={tk.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3, paddingLeft: 4 }}>
              <Box />
              <Txt size={12} style={{ flex: 1 }}>
                {pick(tk.name, wl, 'usted')}
              </Txt>
            </View>
          ))}
        </View>
      ))}
      </View>

      <Heading>{ts(wl, 'checklistSupplies')}</Heading>
      <View nativeID="print-products" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {products.map((p) => (
          <View key={p.id} style={{ width: '50%', flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3 }}>
            <Box />
            <Txt size={12}>{pick(p.name, wl, 'usted')}</Txt>
          </View>
        ))}
      </View>

      <Heading>{ts(wl, 'checklistNotes')}</Heading>
      {[0, 1, 2, 3, 4].map((i) => (
        <View key={i} style={{ height: 24, borderBottomWidth: 1, borderBottomColor: colors.divider }} />
      ))}

      <Txt style={{ fontFamily: fonts.hand, fontSize: 24, lineHeight: 28, textAlign: 'right', marginTop: 16 }} color={colors.primary}>
        {ts(wl, 'thanks')} ♡
      </Txt>
    </Paper>
  );
}

function Heading({ children }: { children: string }) {
  return (
    <View style={{ backgroundColor: colors.background, paddingVertical: 5, paddingHorizontal: 8, marginTop: 10, marginBottom: 6, borderRadius: 3 }}>
      <Txt weight="semibold" size={13}>
        {children}
      </Txt>
    </View>
  );
}
